import { sessionStorageGet } from '@alexis/helpers/sessionStorage';
import { sessionStorageSave } from '@alexis/helpers/sessionStorage';
import useGetPaymentOptions from '@pages/FlightBookingPage/hooks/useGetPaymentOptions';
import { SegmentAncillary, TripDetailsType } from '@pages/FlightBookingPage/types';
import { PaymentOptionsResponse } from '@wego/payments-react-component';
import {
  Fee,
  PaymentCustomerInfo,
  PaymentMethod,
} from '@wego/payments-react-component/dist/types/components/PaymentForm/models';
import { OrderData } from '@wego/payments-react-component/dist/types/helpers/evaluateCustomers';
import { PaymentMethodsResponse } from '@wego/payments-react-component/dist/types/helpers/paymentOptions/models';
import React, { createContext, FC, ReactNode, useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useGetSegmentAncillaries } from '@hooks/api/flights/useGetSegmentAncillaries';

import { FlightBookingDetailsSessionStorage } from '@wegoTypes/flightBooking';

import { PaymentAmountsResponse } from '../_shared/types/PaymentAmounts';

interface BookingPageContextProps {
  selectedPaymentMethod: PaymentMethod | undefined;
  setSelectedPaymentMethod: (paymentMethod: PaymentMethod) => void;
  processingFee?: (Fee & { amountInUsd: number; totalPaymentAmount?: number }) | undefined;
  paymentOptions?: PaymentOptionsResponse[];
  isGettingPaymentOptions?: boolean;
  loggedInUser?: PaymentCustomerInfo;
  primaryRecipient: PaymentCustomerInfo;
  orderData?: OrderData;
  selectedPaymentOption?: PaymentMethodsResponse;
  isSeatSelectionAvailable?: boolean;
  seatSelectionAncillaries?: SegmentAncillary[];
  tripsDetails: TripDetailsType[] | undefined;
  setTripsDetails: React.Dispatch<React.SetStateAction<TripDetailsType[] | undefined>>;
  paymentAmounts: PaymentAmountsResponse | undefined;
  setPaymentAmounts: React.Dispatch<React.SetStateAction<PaymentAmountsResponse | undefined>>;
}
const Context = createContext<BookingPageContextProps>({
  selectedPaymentMethod: undefined,
  setSelectedPaymentMethod: () => {},
  processingFee: undefined,
  paymentOptions: [],
  isGettingPaymentOptions: undefined,
  loggedInUser: undefined,
  primaryRecipient: {
    email: '',
    fullName: '',
    phoneNumber: '',
  },
  orderData: undefined,
  selectedPaymentOption: undefined,
  isSeatSelectionAvailable: false,
  seatSelectionAncillaries: undefined,
  tripsDetails: undefined,
  setTripsDetails: () => {},
  paymentAmounts: undefined,
  setPaymentAmounts: () => {},
});
export const useBookingPageContext = () => useContext<BookingPageContextProps>(Context);
export const BookingPageProvider: FC<{ children?: ReactNode | undefined }> = (props) => {
  const [searchParams] = useSearchParams();
  const orderIdSearchParam = searchParams.get('orderId') ?? '';
  const [paymentAmounts, setPaymentAmounts] = useState<PaymentAmountsResponse>();
  const [tripsDetails, setTripsDetails] = useState<TripDetailsType[] | undefined>(() => {
    const flightBookingDetails =
      sessionStorageGet<FlightBookingDetailsSessionStorage>(orderIdSearchParam);
    return flightBookingDetails?.legDetails || undefined;
  });

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>();

  const { data: ancillaries } = useGetSegmentAncillaries();

  const {
    paymentOptions,
    isLoading: isGettingPaymentOptions,
    loggedInUser,
    primaryRecipient = {
      email: '',
      fullName: '',
      phoneNumber: '',
    },
    orderData,
  } = useGetPaymentOptions(orderIdSearchParam);

  const selectedPaymentOption = useMemo<PaymentMethodsResponse | undefined>(() => {
    if (!selectedPaymentMethod) return undefined;

    const rs = paymentOptions
      .map((option) => option.paymentMethods)
      .flat()
      .find((option) => option.code === selectedPaymentMethod.code);

    const flightBookingDetails =
      sessionStorageGet<FlightBookingDetailsSessionStorage>(orderIdSearchParam) || {};
    sessionStorageSave(orderIdSearchParam, {
      ...flightBookingDetails,
      newSelectedPaymentOption: rs,
    });

    return rs;
  }, [selectedPaymentMethod, paymentOptions]);

  const processingFee = useMemo(() => {
    if (!orderIdSearchParam) {
      return undefined;
    }

    const rs = paymentAmounts?.methods?.find((fee) => {
      if (selectedPaymentMethod?.cardType) {
        return (
          fee.paymentMethodCode === selectedPaymentMethod?.code &&
          fee?.cardType?.toUpperCase() === selectedPaymentMethod?.cardType?.toUpperCase()
        );
      }

      return fee.paymentMethodCode === selectedPaymentMethod?.code;
    });

    const fee = rs
      ? {
          ...(rs.fee || {}),
          amountInUsd: rs?.fee?.amount || 0,
          totalPaymentAmount: rs?.totalPaymentAmount || 0,
        }
      : undefined;

    sessionStorageSave(orderIdSearchParam, {
      ...sessionStorageGet<FlightBookingDetailsSessionStorage>(orderIdSearchParam),
      processingFee: fee,
    });

    return fee;
  }, [selectedPaymentMethod, paymentAmounts?.methods]);

  return (
    <Context.Provider
      value={{
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        processingFee,
        paymentOptions,
        isGettingPaymentOptions,
        loggedInUser,
        primaryRecipient,
        orderData,
        selectedPaymentOption,
        isSeatSelectionAvailable: ancillaries?.isSeatSelectionAvailable,
        seatSelectionAncillaries: ancillaries?.segmentAncillaries,
        tripsDetails,
        setTripsDetails,
        paymentAmounts,
        setPaymentAmounts,
      }}
    >
      {props.children}
    </Context.Provider>
  );
};
